import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { pl, enUS } from 'date-fns/locale';
import { format, parseISO, differenceInYears, subDays } from 'date-fns';
import { SkillLevelCategory } from '@sportschool/shared-types';

export function getActiveUntilDate(resignationDate: string | Date | null | undefined): Date | null {
  if (!resignationDate) return null;
  try {
    const parsed = typeof resignationDate === 'string' ? parseISO(resignationDate) : resignationDate;
    if (isNaN(parsed.getTime())) return null;
    return subDays(parsed, 1);
  } catch {
    return null;
  }
}

export function formatActiveUntilDate(
  resignationDate: string | Date | null | undefined,
  formatStr = 'dd.MM.yyyy',
  options?: Parameters<typeof format>[2]
): string {
  const activeUntil = getActiveUntilDate(resignationDate);
  if (!activeUntil) return '';
  return format(activeUntil, formatStr, options);
}

// Timezone used to present UTC instants (session times etc.). Set by SchoolProvider
// so rendering does not depend on the timezone configured on the viewer's device.
let displayTimeZone = 'Europe/Warsaw';

export function setDisplayTimeZone(timeZone: string | null | undefined): void {
  if (timeZone) displayTimeZone = timeZone;
}

export function getDisplayTimeZone(): string {
  return displayTimeZone;
}

function getZonedParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute'), second: get('second') };
}

// Returns a Date whose local wall-clock fields equal the wall-clock time of `date` in `timeZone`.
// Intended only for formatting with date-fns.
export function toSchoolWallClock(date: Date, timeZone: string = displayTimeZone): Date {
  try {
    const p = getZonedParts(date, timeZone);
    return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, date.getMilliseconds());
  } catch {
    return date;
  }
}

// Formats a UTC instant as a `datetime-local` input value ("yyyy-MM-ddTHH:mm") in the school timezone.
export function toSchoolDateTimeInput(date: Date | string, timeZone: string = displayTimeZone): string {
  const parsed = typeof date === 'string' ? parseISO(date) : date;
  return format(toSchoolWallClock(parsed, timeZone), "yyyy-MM-dd'T'HH:mm");
}

// Interprets a `datetime-local` input value as wall-clock time in the school timezone and returns the UTC instant.
export function schoolDateTimeInputToUtc(value: string, timeZone: string = displayTimeZone): Date {
  const [datePart, timePart = '00:00'] = value.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi);
  const offsetAt = (instant: number) => {
    const p = getZonedParts(new Date(instant), timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(instant / 1000) * 1000;
  };
  try {
    // Second pass corrects the offset when the first guess falls on the other side of a DST switch.
    const guess = wallAsUtc - offsetAt(wallAsUtc);
    return new Date(wallAsUtc - offsetAt(guess));
  } catch {
    return new Date(value);
  }
}

export function formatUtc(
  date: Date | string | null | undefined,
  formatStr: string,
  options?: Parameters<typeof format>[2]
): string {
  if (!date) return '';
  try {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    return format(toSchoolWallClock(parsed), formatStr, options);
  } catch {
    return '';
  }
}

export function calculateAge(dateOfBirth?: string | null): number | null {
  if (!dateOfBirth) return null;
  try {
    const birthDate = parseISO(dateOfBirth);
    if (isNaN(birthDate.getTime())) return null;
    const age = differenceInYears(new Date(), birthDate);
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
}

export function formatAge(age: number, lang = 'pl'): string {
  if (lang.startsWith('pl')) {
    if (age === 1) return '1 rok';
    const mod10 = age % 10;
    const mod100 = age % 100;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
      return `${age} lata`;
    }
    return `${age} lat`;
  }
  return age === 1 ? '1 yr old' : `${age} yrs old`;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getDateLocale = (lang: string) => (lang?.startsWith('pl') ? pl : enUS);

export function getSkillLevelName(level?: { namePl?: string; nameEn?: string; name?: string } | null, lang?: string): string {
  if (!level) return '';
  const isPl = lang?.startsWith('pl');
  return (isPl ? level.namePl : level.nameEn) || level.name || '';
}

/** Children's and youth groups admit only students holding a children's or youth level (mirrors the API rule). */
export function isMinorSkillLevelCategory(category?: SkillLevelCategory | null): boolean {
  return category === SkillLevelCategory.Children || category === SkillLevelCategory.Youth;
}

const DAY_NAME_TO_INDEX: Record<string, number> = {
  sunday: 0,
  niedziela: 0,
  monday: 1,
  poniedziałek: 1,
  poniedzialek: 1,
  tuesday: 2,
  wtorek: 2,
  wednesday: 3,
  środa: 3,
  sroda: 3,
  thursday: 4,
  czwartek: 4,
  friday: 5,
  piątek: 5,
  piatek: 5,
  saturday: 6,
  sobota: 6,
};

export function parseDayOfWeekNum(dayOfWeek: number | string | null | undefined): number | null {
  if (dayOfWeek === null || dayOfWeek === undefined) return null;
  if (typeof dayOfWeek === 'number') {
    return ((Math.floor(dayOfWeek) % 7) + 7) % 7;
  }
  const clean = String(dayOfWeek).trim().toLowerCase();
  if (clean in DAY_NAME_TO_INDEX) {
    return DAY_NAME_TO_INDEX[clean];
  }
  const parsed = parseInt(clean, 10);
  if (!isNaN(parsed)) {
    return ((Math.floor(parsed) % 7) + 7) % 7;
  }
  return null;
}

export function getWeekdayLabel(
  dayOfWeek: number | string | null | undefined,
  type: 'long' | 'short' = 'long',
  lang?: string
): string {
  const normalizedDay = parseDayOfWeekNum(dayOfWeek);
  if (normalizedDay === null) return '';

  try {
    // Reference date: June 28, 2026 is Sunday = 0
    const date = new Date(Date.UTC(2026, 5, 28 + normalizedDay));
    const locale = lang?.startsWith('pl') ? 'pl-PL' : 'en-US';
    const formatter = new Intl.DateTimeFormat(locale, { weekday: type, timeZone: 'UTC' });
    const name = formatter.format(date);
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    return '';
  }
}

export function getEveryWeekdayLabel(
  dayOfWeek: number | string | null | undefined,
  lang?: string
): string {
  const normalizedDay = parseDayOfWeekNum(dayOfWeek);
  if (normalizedDay === null) return '';

  const isPl = lang?.startsWith('pl');
  const label = getWeekdayLabel(normalizedDay, 'long', lang);
  if (!label) return '';

  if (!isPl) {
    return `Every ${label}`;
  }

  // Polish grammar: "Każdy" vs "Każda" (Środa = 3, Sobota = 6, Niedziela = 0 are feminine)
  const isFeminine = normalizedDay === 3 || normalizedDay === 6 || normalizedDay === 0;
  const prefix = isFeminine ? 'Każda' : 'Każdy';
  return `${prefix} ${label.toLowerCase()}`;
}

export function generatePlayerScheduleTitle(
  schedule?: { dayOfWeek?: number | string | null; startTime?: string; name?: string } | null,
  locationName?: string | null,
  lang?: string
): string {
  if (!schedule) return '';
  const dayName = getWeekdayLabel(schedule.dayOfWeek, 'long', lang);
  const time = schedule.startTime ? schedule.startTime.slice(0, 5) : '';
  const loc = locationName ? locationName.trim() : '';

  const parts = [dayName, time, loc].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' ');
  }
  return schedule.name || '';
}

export function formatExternalUrl(url?: string | null): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

/**
 * Checks if the given medical survey field text contains any meaningful medical contraindications or alerts.
 * Returns false if the value is null, undefined, empty, or contains negative indicator words (e.g. "nie", "brak", "none").
 */
export function isMeaningfulMedicalValue(text: string | null | undefined): boolean {
  if (!text) return false;
  const clean = text.trim().replace(/^[.\s-]+|[.\s-]+$/g, '').toLowerCase();
  if (!clean) return false;

  const negatives = new Set([
    'nie',
    'brak',
    'none',
    'nie dotyczy',
    'nie mam',
    'brak uwag',
    'brak przeciwwskazań',
    'brak przeciwwskazan',
    'nd',
    'n/a',
    'no',
    'nothing',
    'niema'
  ]);

  return !negatives.has(clean);
}

export interface DaysUntilSessionInfo {
  days: number;
  label: string;
  badgeClass: string;
}

/**
 * Calculates days remaining until a session and returns badge metadata & localized label.
 * Color coding rules:
 * - Red (under 3 days / today / past): < 3 days
 * - Orange (under 10 days): 3 to 9 days
 * - Green (remaining cases): >= 10 days
 */
export function getDaysUntilSessionInfo(sessionStartsAt?: string | Date | null): DaysUntilSessionInfo | null {
  if (!sessionStartsAt) return null;
  try {
    const sessionDate = typeof sessionStartsAt === 'string' ? parseISO(sessionStartsAt) : sessionStartsAt;
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfSession = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
    
    const diffTime = startOfSession.getTime() - startOfToday.getTime();
    const daysDiff = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (daysDiff < 0) {
      return {
        days: daysDiff,
        label: 'W przeszłości',
        badgeClass: 'bg-rose-500/10 text-rose-600 border-rose-500/20 hover:bg-rose-500/10 dark:bg-rose-500/20 dark:text-rose-400 font-bold',
      };
    }
    if (daysDiff === 0) {
      return {
        days: 0,
        label: 'Dzisiaj',
        badgeClass: 'bg-rose-500/10 text-rose-600 border-rose-500/20 hover:bg-rose-500/10 dark:bg-rose-500/20 dark:text-rose-400 font-bold',
      };
    }
    if (daysDiff < 3) {
      return {
        days: daysDiff,
        label: `Za ${daysDiff} ${daysDiff === 1 ? 'dzień' : 'dni'}`,
        badgeClass: 'bg-rose-500/10 text-rose-600 border-rose-500/20 hover:bg-rose-500/10 dark:bg-rose-500/20 dark:text-rose-400 font-bold',
      };
    }
    if (daysDiff < 10) {
      return {
        days: daysDiff,
        label: `Za ${daysDiff} dni`,
        badgeClass: 'bg-amber-500/10 text-amber-600 border-amber-500/20 hover:bg-amber-500/10 dark:bg-amber-500/20 dark:text-amber-400 font-bold',
      };
    }
    return {
      days: daysDiff,
      label: `Za ${daysDiff} dni`,
      badgeClass: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 hover:bg-emerald-500/10 dark:bg-emerald-500/20 dark:text-emerald-400 font-bold',
    };
  } catch {
    return null;
  }
}

/**
 * Calculates age in full years from a date of birth.
 * Returns null if birthdate is invalid or missing.
 */
export function calculateAgeInYears(dateOfBirth?: string | Date | null): number | null {
  if (!dateOfBirth) return null;
  try {
    const birthDate = typeof dateOfBirth === 'string' ? parseISO(dateOfBirth) : dateOfBirth;
    if (isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
}

export interface NavigationLocationTarget {
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  district?: string | null;
  city?: string | null;
  name?: string | null;
}

/**
 * Returns a navigation URL respecting the user's preferred navigation provider (if set in localStorage),
 * or falling back to Google Maps by default (universal across all platforms).
 */
export function getNavigationUrl(location?: NavigationLocationTarget | null): string {
  if (!location) return 'https://www.google.com/maps';

  try {
    const saved = localStorage.getItem('preferred_navigation_provider');
    if (saved === 'apple') {
      const hasCoords =
        typeof location.latitude === 'number' &&
        typeof location.longitude === 'number' &&
        !isNaN(location.latitude) &&
        !isNaN(location.longitude);
      const coordsStr = hasCoords ? `${location.latitude},${location.longitude}` : '';
      const addressParts = [
        location.name,
        location.address,
        location.district,
        location.city,
      ].filter(Boolean);
      const queryStr = addressParts.join(', ') || location.name || 'Lokalizacja';

      if (hasCoords) {
        return `https://maps.apple.com/?daddr=${coordsStr}&q=${encodeURIComponent(location.name || queryStr)}`;
      }
      return `https://maps.apple.com/?daddr=${encodeURIComponent(queryStr)}`;
    }

    if (saved === 'waze') {
      const hasCoords =
        typeof location.latitude === 'number' &&
        typeof location.longitude === 'number' &&
        !isNaN(location.latitude) &&
        !isNaN(location.longitude);
      const coordsStr = hasCoords ? `${location.latitude},${location.longitude}` : '';
      const addressParts = [
        location.name,
        location.address,
        location.district,
        location.city,
      ].filter(Boolean);
      const queryStr = addressParts.join(', ') || location.name || 'Lokalizacja';

      if (hasCoords) {
        return `https://waze.com/ul?ll=${coordsStr}&navigate=yes`;
      }
      return `https://waze.com/ul?q=${encodeURIComponent(queryStr)}&navigate=yes`;
    }
  } catch {
    // Ignore storage errors
  }

  // Default to Google Maps (universal across all platforms)
  const hasCoords =
    typeof location.latitude === 'number' &&
    typeof location.longitude === 'number' &&
    !isNaN(location.latitude) &&
    !isNaN(location.longitude);
  const coordsStr = hasCoords ? `${location.latitude},${location.longitude}` : '';
  const addressParts = [
    location.name,
    location.address,
    location.district,
    location.city,
  ].filter(Boolean);
  const queryStr = addressParts.join(', ') || location.name || 'Lokalizacja';

  if (hasCoords) {
    return `https://www.google.com/maps/dir/?api=1&destination=${coordsStr}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(queryStr)}`;
}

export function formatStudentSessionTitle(
  startsAt: Date | string | null | undefined,
  locationName?: string | null,
  lang: string = 'pl'
): string {
  if (!startsAt) return '';
  const dayName = formatUtc(startsAt, 'EEEE', { locale: getDateLocale(lang) });
  const capitalizedDay = dayName ? dayName.charAt(0).toUpperCase() + dayName.slice(1) : '';
  const time = formatUtc(startsAt, 'HH:mm');
  const loc = locationName?.trim();
  return loc ? `${capitalizedDay} ${time} ${loc}` : `${capitalizedDay} ${time}`;
}

export function formatStudentScheduleTitle(
  dayOfWeek: number | string | null | undefined,
  startTime: string | null | undefined,
  locationName?: string | null,
  lang: string = 'pl'
): string {
  const dayName = getWeekdayLabel(dayOfWeek, 'long', lang);
  const time = startTime ? startTime.slice(0, 5) : '';
  const loc = locationName?.trim();
  return loc ? `${dayName} ${time} ${loc}` : `${dayName} ${time}`;
}


